import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Chip
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  SaveOutlined,
  CheckCircleOutlined,
  ErrorOutlined,
  InfoOutlined,
  SecurityOutlined,
  BusinessOutlined as BusinessIcon,
  LocationOnOutlined as LocationIcon,
  DescriptionOutlined as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import * as fiscalService from "../../services/fiscal";

const useStyles = makeStyles((theme) => ({
  settingContainer: {
    padding: '20px',
    border: '1px solid',
    borderColor: theme.palette.type === 'dark' ? '#444' : '#e0e0e0',
    borderRadius: '8px',
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#ffffff',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: '8px'
  },
  sectionIcon: {
    marginRight: '8px',
    color: theme.palette.primary.main,
  },
  button: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  validationCard: {
    marginTop: theme.spacing(2),
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  certificateInfo: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
  },
  statusChip: {
    fontSize: '0.75rem',
    height: '20px',
    marginLeft: theme.spacing(0.5)
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  }
}));

const FiscalConfig = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    validation: true,
    company: true,
    address: false,
    nfe: false,
    certificate: false
  });
  const [config, setConfig] = useState({
    cnpj: "",
    stateRegistration: "",
    municipalRegistration: "",
    fiscalRegime: "SIMPLES_NACIONAL",
    zipCode: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
    cityCode: "",
    stateCode: "",
    nfeEnvironment: "HOMOLOGACAO",
    nfeSeries: 1,
    digitalCertificate: "",
    certificatePassword: "",
    certificateExpiration: "",
    autoSendToNapp: false,
    autoGenerateNfe: false,
    nfeEmailFrom: "",
    autoSendNfeByEmail: true,
    defaultFiscalObservations: "",
  });
  const [validation, setValidation] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);

  const fiscalRegimes = [
    { value: "SIMPLES_NACIONAL", label: i18n.t("fiscal.company.regimes.SIMPLES_NACIONAL") },
    { value: "LUCRO_PRESUMIDO", label: i18n.t("fiscal.company.regimes.LUCRO_PRESUMIDO") },
    { value: "LUCRO_REAL", label: i18n.t("fiscal.company.regimes.LUCRO_REAL") },
    { value: "MEI", label: i18n.t("fiscal.company.regimes.MEI") },
  ];

  const nfeEnvironments = [
    { value: "HOMOLOGACAO", label: i18n.t("fiscal.nfe.environments.HOMOLOGACAO") },
    { value: "PRODUCAO", label: i18n.t("fiscal.nfe.environments.PRODUCAO") },
  ];

  useEffect(() => {
    if (user?.companyId) {
      loadFiscalConfig();
      validateFiscalReadiness();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadFiscalConfig = useCallback(async () => {
    try {
      const response = await fiscalService.getFiscalConfig(user.companyId);
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toastError(error);
      }
    } finally {
      setLoading(false);
    }
  }, [user.companyId]);

  const validateFiscalReadiness = useCallback(async () => {
    if (!user?.companyId) return;

    setValidating(true);
    try {
      const response = await fiscalService.validateFiscalReadiness(user.companyId);
      setValidation(response.data.data);
    } catch (error) {
      console.error("Erro ao validar configuração fiscal:", error);
    } finally {
      setValidating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const handleInputChange = useCallback((field) => (event) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      [field]: event.target.value,
    }));
  }, []);

  const handleSwitchChange = useCallback((field) => (event) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      [field]: event.target.checked,
    }));
  }, []);

  const handleCertificateFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCertificateFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(",")[1];
        setConfig({
          ...config,
          digitalCertificate: base64,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await fiscalService.saveFiscalConfig(user.companyId, config);
      toast.success(i18n.t("fiscal.messages.saveSuccess"));
      await validateFiscalReadiness();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setValidating(true);
    try {
      await fiscalService.processAutomaticInvoicing();
      toast.success(i18n.t("fiscal.messages.testSuccess"));
      await validateFiscalReadiness();
    } catch (error) {
      toastError(error);
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.settingContainer}>
        <Box className={classes.sectionHeader}>
          <BusinessIcon className={classes.sectionIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t("fiscal.title")}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {i18n.t("fiscal.description")}
        </Typography>

        {validation && (
          <Grid container spacing={1}>
            <Grid item>
              <Chip
                icon={validation.isReady ? <CheckCircleOutlined /> : <ErrorOutlined />}
                label={validation.isReady ? i18n.t("fiscal.status.ready") : i18n.t("fiscal.status.incomplete")}
                color={validation.isReady ? 'primary' : 'default'}
                variant={validation.isReady ? 'default' : 'outlined'}
                className={classes.statusChip}
              />
            </Grid>
            {!validation.isReady && (
              <Grid item>
                <Chip
                  icon={<ErrorOutlined />}
                  label={`${validation.missingFields.length} ${i18n.t("fiscal.status.missingFields")}`}
                  color="default"
                  variant="outlined"
                  className={classes.statusChip}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      {/* Status de Validação */}
      {validation && (
        <Card style={{ marginBottom: '16px' }}>
          <CardHeader
            avatar={validation.isReady ? <CheckCircleOutlined className={classes.successIcon} /> : <ErrorOutlined className={classes.errorIcon} />}
            title={validation.isReady ? i18n.t("fiscal.validation.ready") : i18n.t("fiscal.validation.incomplete")}
            subheader={validation.isReady ? i18n.t("fiscal.validation.readyDesc") : `${validation.missingFields.length} ${i18n.t("fiscal.validation.incompleteDesc")}`}
            action={
              <IconButton onClick={() => toggleSection('validation')}>
                {expandedSections.validation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          />
          <Collapse in={expandedSections.validation}>
            <CardContent>
              {!validation.isReady && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {i18n.t("fiscal.validation.missingFields")}
                  </Typography>
                  <ul>
                    {validation.missingFields.map((field, index) => (
                      <li key={index}>{field}</li>
                    ))}
                  </ul>
                </Box>
              )}
              {validation.warnings.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {i18n.t("fiscal.validation.warnings")}
                  </Typography>
                  {validation.warnings.map((warning, index) => (
                    <Alert key={index} severity="warning" style={{ marginTop: 8 }}>
                      {warning}
                    </Alert>
                  ))}
                </Box>
              )}
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Dados da Empresa */}
      <Card style={{ marginBottom: '16px' }}>
        <CardHeader
          avatar={<BusinessIcon />}
          title={i18n.t("fiscal.company.title")}
          action={
            <IconButton onClick={() => toggleSection('company')}>
              {expandedSections.company ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.company}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={`${i18n.t("fiscal.company.cnpj")} *`}
                  value={config.cnpj}
                  onChange={handleInputChange("cnpj")}
                  fullWidth
                  placeholder="00.000.000/0000-00"
                  inputProps={{ maxLength: 18 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={i18n.t("fiscal.company.stateRegistration")}
                  value={config.stateRegistration}
                  onChange={handleInputChange("stateRegistration")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={i18n.t("fiscal.company.municipalRegistration")}
                  value={config.municipalRegistration}
                  onChange={handleInputChange("municipalRegistration")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{`${i18n.t("fiscal.company.fiscalRegime")} *`}</InputLabel>
                  <Select
                    value={config.fiscalRegime}
                    onChange={handleInputChange("fiscalRegime")}
                  >
                    {fiscalRegimes.map((regime) => (
                      <MenuItem key={regime.value} value={regime.value}>
                        {regime.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Endereço Fiscal */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <LocationIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
          {i18n.t("fiscal.address.title")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label={`${i18n.t("fiscal.address.zipCode")} *`}
              value={config.zipCode}
              onChange={handleInputChange("zipCode")}
              fullWidth
              placeholder="00000-000"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={`${i18n.t("fiscal.address.street")} *`}
              value={config.street}
              onChange={handleInputChange("street")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label={`${i18n.t("fiscal.address.number")} *`}
              value={config.number}
              onChange={handleInputChange("number")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={i18n.t("fiscal.address.complement")}
              value={config.complement}
              onChange={handleInputChange("complement")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={`${i18n.t("fiscal.address.neighborhood")} *`}
              value={config.neighborhood}
              onChange={handleInputChange("neighborhood")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={`${i18n.t("fiscal.address.city")} *`}
              value={config.city}
              onChange={handleInputChange("city")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label={`${i18n.t("fiscal.address.state")} *`}
              value={config.state}
              onChange={handleInputChange("state")}
              fullWidth
              placeholder="SP"
              inputProps={{ maxLength: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label={`${i18n.t("fiscal.address.cityCode")} *`}
              value={config.cityCode}
              onChange={handleInputChange("cityCode")}
              fullWidth
              placeholder="3550308"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label={`${i18n.t("fiscal.address.stateCode")} *`}
              value={config.stateCode}
              onChange={handleInputChange("stateCode")}
              fullWidth
              placeholder="35"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Configurações NFe */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <DescriptionIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
          {i18n.t("fiscal.nfe.title")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{`${i18n.t("fiscal.nfe.environment")} *`}</InputLabel>
              <Select
                value={config.nfeEnvironment}
                onChange={handleInputChange("nfeEnvironment")}
              >
                {nfeEnvironments.map((env) => (
                  <MenuItem key={env.value} value={env.value}>
                    {env.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label={`${i18n.t("fiscal.nfe.series")} *`}
              type="number"
              value={config.nfeSeries}
              onChange={handleInputChange("nfeSeries")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label={i18n.t("fiscal.nfe.emailFrom")}
              value={config.nfeEmailFrom}
              onChange={handleInputChange("nfeEmailFrom")}
              fullWidth
              type="email"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={i18n.t("fiscal.nfe.observations")}
              value={config.defaultFiscalObservations}
              onChange={handleInputChange("defaultFiscalObservations")}
              fullWidth
              multiline
              rows={3}
              placeholder={i18n.t("fiscal.nfe.observationsPlaceholder")}
            />
          </Grid>
        </Grid>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Switch
                checked={config.autoGenerateNfe}
                onChange={handleSwitchChange("autoGenerateNfe")}
                color="primary"
              />
            }
            label={i18n.t("fiscal.nfe.autoGenerate")}
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.autoSendNfeByEmail}
                onChange={handleSwitchChange("autoSendNfeByEmail")}
                color="primary"
              />
            }
            label={i18n.t("fiscal.nfe.autoEmail")}
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.autoSendToNapp}
                onChange={handleSwitchChange("autoSendToNapp")}
                color="primary"
              />
            }
            label={i18n.t("fiscal.nfe.autoNapp")}
          />
        </Box>
      </Paper>

      {/* Certificado Digital */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <SecurityOutlined style={{ marginRight: 8, verticalAlign: "middle" }} />
          {i18n.t("fiscal.certificate.title")}
        </Typography>

        <Alert severity="info" style={{ marginBottom: 16 }}>
          <strong>{i18n.t("fiscal.certificate.info")}</strong>
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <input
              accept=".p12,.pfx"
              style={{ display: "none" }}
              id="certificate-file"
              type="file"
              onChange={handleCertificateFileChange}
            />
            <label htmlFor="certificate-file">
              <Button variant="outlined" component="span" fullWidth>
                {certificateFile ? certificateFile.name : `${i18n.t("fiscal.certificate.selectFile")} *`}
              </Button>
            </label>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={`${i18n.t("fiscal.certificate.password")} *`}
              type="password"
              value={config.certificatePassword}
              onChange={handleInputChange("certificatePassword")}
              fullWidth
            />
          </Grid>
          {config.certificateExpiration && (
            <Grid item xs={12}>
              <Box className={classes.certificateInfo}>
                <Typography variant="body2">
                  <InfoOutlined style={{ marginRight: 4, verticalAlign: "middle" }} />
                  <strong>{i18n.t("fiscal.certificate.validity")}</strong>{" "}
                  {new Date(config.certificateExpiration).toLocaleDateString("pt-BR")}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Botões de Ação */}
      <Paper className={classes.paper}>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={validating}
            className={classes.button}
          >
            {validating ? <CircularProgress size={20} /> : i18n.t("fiscal.actions.testConnection")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveOutlined />}
            className={classes.button}
          >
            {saving ? i18n.t("fiscal.actions.saving") : i18n.t("fiscal.actions.save")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FiscalConfig;